import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import compactLogo from "../images/bossdrop_logo_transparent_512.png";
import { SHOW_MEMORY_PAGE } from "../lib/navFlags.js";
import { NavCountBadge } from "./PendingApprovals";

function ControlRoomIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 6.5h14M5 12h14M5 17.5h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
      <circle cx="8" cy="6.5" r="1.6" fill="currentColor" />
      <circle cx="16" cy="12" r="1.6" fill="currentColor" />
      <circle cx="11" cy="17.5" r="1.6" fill="currentColor" />
    </svg>
  );
}

function KpiIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 18.5V10.5M12 18.5V6.5M19 18.5V13.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M4 18.5h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        opacity="0.7"
      />
    </svg>
  );
}

function MemoryIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <ellipse
        cx="12"
        cy="6.5"
        rx="7"
        ry="2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M5 6.5v5.5c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8V6.5M5 12v5.5c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8V12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AuditLogsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect
        x="5"
        y="4.5"
        width="14"
        height="15"
        rx="2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M8.5 9h7M8.5 12h7M8.5 15h4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Last in the nav: this one is about the person, not the workspace.
function AccountIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12"
        cy="8.6"
        r="3.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M5.5 19.5a6.5 6.5 0 0 1 13 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Platform admins only, and last in the nav: two buildings, because this one
// is about other companies' workspaces rather than anything in this one.
// Same 24x24 grid, stroked in currentColor, as the rest of the set.
function ClientsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4.5 19.5V6.3a1.8 1.8 0 0 1 1.8-1.8h5.4a1.8 1.8 0 0 1 1.8 1.8v13.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 10.5h4.2a1.8 1.8 0 0 1 1.8 1.8v7.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M3.5 19.5h17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M7.5 8.5h3M7.5 12.2h3M7.5 15.9h3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        opacity="0.75"
      />
    </svg>
  );
}

function NavItem({ to, children, icon, alsoActive = false, badge = 0 }) {
  return (
    <NavLink
      to={to}
      title={children}
      className={({ isActive }) =>
        `navItem ${isActive || alsoActive ? "active" : ""}${
          badge ? " navItem--withBadge" : ""
        }`
      }
    >
      <span className="navItem__icon">{icon}</span>
      <span className="navItem__label">{children}</span>
      <NavCountBadge count={badge} />
    </NavLink>
  );
}

export default function Sidebar({
  logoSrc,
  open = true,
  isPlatformAdmin = false,
  // Pending approvals, only ever non-zero for someone who may approve them.
  pendingApprovalsCount = 0,
}) {
  const { pathname } = useLocation();
  const controlRoomAlsoActive = pathname === "/";
  const activeLogoSrc = open ? logoSrc : compactLogo;

  return (
    <aside className={`sidebar${open ? "" : " sidebar--compact"}`}>
      <div className="logoWrap">
        <img
          src={activeLogoSrc}
          alt="BossDrop"
          onError={(e) => (e.currentTarget.style.visibility = "hidden")}
        />
        <div className="brandName">BossDrop.ai</div>
      </div>

      <nav className="nav">
        <NavItem
          to="/control-room"
          alsoActive={controlRoomAlsoActive}
          icon={<ControlRoomIcon />}
        >
          Control Room
        </NavItem>

        {/* Memory can be switched off (lib/navFlags.js). */}
        {SHOW_MEMORY_PAGE ? (
          <NavItem to="/memory" icon={<MemoryIcon />}>
            Memory
          </NavItem>
        ) : null}
        <NavItem
          to="/audit-logs"
          icon={<AuditLogsIcon />}
          badge={pendingApprovalsCount}
        >
          Audit Logs
        </NavItem>
        <NavItem to="/kpis" icon={<KpiIcon />}>
          KPIs
        </NavItem>
        <NavItem to="/account" icon={<AccountIcon />}>
          Account
        </NavItem>
        {/* BossDrop's own admin area, not a workspace page, so it sits after
            everything else and only a platform admin is shown it. The route
            and the endpoint behind it both check again; this only keeps the
            door out of everyone else's way. */}
        {isPlatformAdmin ? (
          <NavItem to="/clients" icon={<ClientsIcon />}>
            Clients
          </NavItem>
        ) : null}
      </nav>

      <div className="sidebarFooter">
        <span className="sidebarFooter__icon">BD</span>
        <span className="sidebarFooter__label">
          © {new Date().getFullYear()} BossDrop
        </span>
      </div>
    </aside>
  );
}
